import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CropOutlined, FitScreenOutlined,
  ImageOutlined,
  FormatAlignLeftOutlined, FormatAlignCenterOutlined, FormatAlignRightOutlined,
} from '@mui/icons-material';
import { ContentBrowserModal } from '../ContentBrowserModal';

// ── PortalPopover ─────────────────────────────────────────────────────────────
export function PortalPopover({ anchorRef, onClose, children, suppressClose = false, centerAnchor = false }) {
  const popRef   = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    if (centerAnchor) {
      setPos({ top: r.top + r.height / 2, left: r.left + r.width / 2, centerAnchor: true });
    } else {
      setPos({ bottom: window.innerHeight - r.top + 6, left: r.left });
    }
  }, [centerAnchor]);

  useEffect(() => {
    const handler = e => {
      if (suppressClose) return;
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [onClose, anchorRef, suppressClose]);

  if (!pos) return null;

  const popStyle = pos.centerAnchor
    ? {
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: 'translate(-50%, -50%)',
        background: '#fff', border: '1px solid #e4e9f0',
        borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.20)',
        padding: '12px 14px', zIndex: 99, minWidth: 180,
      }
    : {
        position: 'fixed', bottom: pos.bottom, left: pos.left,
        background: '#fff', border: '1px solid #e4e9f0',
        borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
        padding: '12px 14px', zIndex: 99, minWidth: 180,
      };

  return ReactDOM.createPortal(
    <div
      ref={popRef}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      style={popStyle}
    >
      {children}
    </div>,
    document.body
  );
}

// ── ColorOpacityPopover ───────────────────────────────────────────────────────
export function ColorOpacityPopover({ label, color, opacity, onColorChange, onOpacityChange, onClose, anchorRef, hideOpacity }) {
  return (
    <PortalPopover anchorRef={anchorRef} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </div>
        <div onClick={onClose} style={{ fontSize: 14, lineHeight: 1, color: '#b0bac8', cursor: 'pointer', fontWeight: 700 }}>✕</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: hideOpacity ? 0 : 12 }}>
        <input
          type="color"
          value={color}
          onChange={e => onColorChange(e.target.value)}
          style={{ width: 32, height: 32, border: '1.5px solid #e4e9f0', borderRadius: 6, padding: 2, cursor: 'pointer', background: 'none' }}
        />
        <input
          type="text"
          value={color}
          onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onColorChange(e.target.value); }}
          style={{ flex: 1, border: '1px solid #e4e9f0', borderRadius: 6, padding: '5px 8px', fontSize: 12, fontFamily: 'monospace', color: '#2c3444', outline: 'none' }}
        />
      </div>
      {!hideOpacity && <>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          Transparency
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="range" min="0" max="1" step="0.01"
            value={opacity}
            onChange={e => onOpacityChange(parseFloat(e.target.value))}
            style={{ flex: 1, accentColor: '#7c3aed', cursor: 'pointer', height: 4 }}
          />
          <div style={{ width: 44, height: 28, border: '1px solid #e4e9f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#2c3444' }}>
            {Math.round(opacity * 100)}
          </div>
        </div>
      </>}
    </PortalPopover>
  );
}

// ── WidthPopover ──────────────────────────────────────────────────────────────
export function WidthPopover({ value, onChange, onClose, anchorRef }) {
  const [inputVal, setInputVal] = useState(String(value));

  useEffect(() => { setInputVal(String(value)); }, [value]);

  const commit = val => {
    const n = parseFloat(val);
    if (!isNaN(n) && n >= 0 && n <= 16) onChange(n);
  };

  return (
    <PortalPopover anchorRef={anchorRef} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Border Width
        </div>
        <div onClick={onClose} style={{ fontSize: 14, lineHeight: 1, color: '#b0bac8', cursor: 'pointer', fontWeight: 700 }}>✕</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="range" min="0" max="16" step="0.5"
          value={value}
          onChange={e => { onChange(parseFloat(e.target.value)); setInputVal(e.target.value); }}
          style={{ flex: 1, accentColor: '#7c3aed', cursor: 'pointer', height: 4 }}
        />
        <input
          type="number" min="0" max="16" step="0.5"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onBlur={e => commit(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(e.target.value); }}
          style={{ width: 44, height: 28, border: '1px solid #e4e9f0', borderRadius: 6, textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#2c3444', outline: 'none', fontFamily: 'inherit' }}
        />
      </div>
    </PortalPopover>
  );
}

// ── ImagePopover ──────────────────────────────────────────────────────────────
export function ImagePopover({ hotspot, onUpdate, onClose, anchorRef, centerAnchor = false }) {
  const [browserOpen, setBrowserOpen] = useState(false);
  const meta    = hotspot?.meta || {};
  const stopAll = e => e.stopPropagation();

  const updMeta = patch => {
    if (!hotspot) return;
    onUpdate({ ...hotspot, meta: { ...meta, ...patch } });
  };

  const imageUrl  = meta.imageUrl  ?? null;
  const objectFit = meta.objectFit ?? 'cover';

  return (
    <>
      <PortalPopover anchorRef={anchorRef} onClose={onClose} suppressClose={browserOpen} centerAnchor={centerAnchor}>
        <Box onMouseDown={stopAll} onClick={stopAll} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 220 }}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ fontSize: 10, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Image</Box>
            <Box onClick={e => { stopAll(e); onClose(); }} sx={{ fontSize: 14, lineHeight: 1, color: '#b0bac8', cursor: 'pointer', fontWeight: 700, '&:hover': { color: '#5a6474' } }}>✕</Box>
          </Box>

          {/* Image preview + select */}
          <Box
            onClick={e => { stopAll(e); setBrowserOpen(true); }}
            sx={{
              height: 80, borderRadius: '8px', overflow: 'hidden',
              border: '1.5px dashed #e4e9f0', cursor: 'pointer',
              background: imageUrl ? 'transparent' : '#f8f9fb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
              transition: 'border-color 0.15s, background 0.15s',
              '&:hover': { borderColor: '#e67e22', background: imageUrl ? 'transparent' : alpha('#e67e22', 0.04) },
            }}
          >
            {imageUrl ? (
              <>
                <Box component="img" src={imageUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
                  <Box sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>Change image</Box>
                </Box>
                <Box
                  onClick={e => { e.stopPropagation(); updMeta({ imageUrl: null }); }}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', zIndex: 2,
                    '&:hover': { background: '#dc2626' },
                  }}
                >
                  <Box sx={{ fontSize: 9, fontWeight: 800, color: '#fff', lineHeight: 1 }}>✕</Box>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <ImageOutlined sx={{ fontSize: 22, color: '#b0bac8' }} />
                <Box sx={{ fontSize: 11, fontWeight: 600, color: '#9ea8b3' }}>Select image</Box>
              </Box>
            )}
          </Box>

          {/* Object fit */}
          <Box>
            <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fit</Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[
                { value: 'cover',   label: 'Cover',   icon: <CropOutlined sx={{ fontSize: 14 }} /> },
                { value: 'contain', label: 'Contain', icon: <FitScreenOutlined sx={{ fontSize: 14 }} /> },
              ].map(opt => (
                <Box
                  key={opt.value}
                  onClick={e => { stopAll(e); updMeta({ objectFit: opt.value }); }}
                  sx={{
                    flex: 1, height: 34, borderRadius: '7px', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px',
                    border: `1px solid ${objectFit === opt.value ? '#e67e22' : '#e4e9f0'}`,
                    background: objectFit === opt.value ? alpha('#e67e22', 0.07) : '#f8f9fb',
                    color: objectFit === opt.value ? '#e67e22' : '#9ea8b3',
                    transition: 'all 0.12s',
                    '&:hover': { borderColor: alpha('#e67e22', 0.5), color: '#e67e22' },
                  }}
                >
                  {opt.icon}
                  <Box sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.03em' }}>{opt.label}</Box>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
      </PortalPopover>

      <ContentBrowserModal
        open={browserOpen}
        onClose={() => setBrowserOpen(false)}
        onSelect={(url) => { updMeta({ imageUrl: url }); setBrowserOpen(false); onClose(); }}
        mediaType="image"
      />
    </>
  );
}

// ── FontPopover ───────────────────────────────────────────────────────────────
export function FontPopover({ hotspot, onUpdate, onClose, anchorRef, breakpoint = 'desktop' }) {
  const meta      = hotspot?.meta || {};
  const stopAll   = e => e.stopPropagation();
  const isDesktop = !breakpoint || breakpoint === 'desktop';

  // Resolved values: bp override takes priority, desktop meta is the fallback
  const bpTextStyle = isDesktop ? {} : (hotspot?.responsive?.[breakpoint]?.textStyle || {});
  const fontSize    = bpTextStyle.fontSize   ?? meta.fontSize   ?? 25;
  const fontWeight  = bpTextStyle.fontWeight ?? meta.fontWeight ?? 600;
  const color       = bpTextStyle.color      ?? meta.color      ?? '#ffffff';
  const textAlign   = bpTextStyle.textAlign  ?? meta.textAlign  ?? 'center';
  const hasOverride = !isDesktop && Object.keys(bpTextStyle).length > 0;

  const updFont = patch => {
    if (!hotspot) return;
    if (isDesktop) {
      onUpdate({ ...hotspot, meta: { ...meta, ...patch } });
    } else {
      const existing    = hotspot?.responsive?.[breakpoint] || {};
      const existingTs  = existing.textStyle || {};
      onUpdate({
        ...hotspot,
        responsive: {
          ...(hotspot.responsive || {}),
          [breakpoint]: { ...existing, textStyle: { ...existingTs, ...patch } },
        },
      });
    }
  };

  const resetOverride = e => {
    stopAll(e);
    if (!hotspot || isDesktop) return;
    const existing = hotspot?.responsive?.[breakpoint] || {};
    const { textStyle: _ts, ...rest } = existing;
    onUpdate({
      ...hotspot,
      responsive: {
        ...(hotspot.responsive || {}),
        [breakpoint]: rest,
      },
    });
  };

  const fontWeightOptions = [
    { value: 400, label: 'Regular' },
    { value: 600, label: 'Semi Bold' },
    { value: 700, label: 'Bold' },
    { value: 800, label: 'Extra Bold' },
  ];

  const alignOptions = [
    { value: 'left',   icon: <FormatAlignLeftOutlined sx={{ fontSize: 15 }} /> },
    { value: 'center', icon: <FormatAlignCenterOutlined sx={{ fontSize: 15 }} /> },
    { value: 'right',  icon: <FormatAlignRightOutlined sx={{ fontSize: 15 }} /> },
  ];

  return (
    <PortalPopover anchorRef={anchorRef} onClose={onClose}>
      <Box onMouseDown={stopAll} onClick={stopAll} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 220 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ fontSize: 10, fontWeight: 700, color: '#9ea8b3', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Font</Box>
            {!isDesktop && (
              <Box sx={{ px: 0.75, py: 0.1, borderRadius: '4px', background: hasOverride ? alpha('#f59e0b', 0.12) : '#f0f4f8', border: `1px solid ${hasOverride ? '#fcd34d' : '#e4e9f0'}`, fontSize: 9, fontWeight: 700, color: hasOverride ? '#b45309' : '#9ea8b3', letterSpacing: '0.04em' }}>
                {breakpoint.toUpperCase().replace('-', ' ')}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasOverride && (
              <Box onClick={resetOverride} sx={{ fontSize: 9, fontWeight: 700, color: '#dc2626', cursor: 'pointer', px: 0.75, py: 0.2, borderRadius: '4px', background: '#fee2e2', border: '1px solid #fca5a5', '&:hover': { background: '#fecaca' } }}>
                Reset
              </Box>
            )}
            <Box onClick={onClose} sx={{ fontSize: 14, lineHeight: 1, color: '#b0bac8', cursor: 'pointer', fontWeight: 700, '&:hover': { color: '#5a6474' } }}>✕</Box>
          </Box>
        </Box>

        {/* Font size */}
        <Box>
          <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Size</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="range" min="8" max="96" step="1"
              value={fontSize}
              onChange={e => updFont({ fontSize: parseFloat(e.target.value) })}
              style={{ flex: 1, accentColor: '#7c3aed', cursor: 'pointer', height: 4 }}
            />
            <Box sx={{ width: 42, height: 28, border: '1px solid #e4e9f0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#2c3444', userSelect: 'none', flexShrink: 0 }}>
              {fontSize}
            </Box>
          </Box>
        </Box>

        {/* Font weight */}
        <Box>
          <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Weight</Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {fontWeightOptions.map(opt => (
              <Box
                key={opt.value}
                onClick={() => updFont({ fontWeight: opt.value })}
                sx={{
                  flex: 1, py: 0.5, borderRadius: '6px', cursor: 'pointer',
                  fontSize: 10, fontWeight: opt.value, textAlign: 'center',
                  border: `1px solid ${fontWeight === opt.value ? '#7c3aed' : '#e4e9f0'}`,
                  background: fontWeight === opt.value ? alpha('#7c3aed', 0.08) : '#f8f9fb',
                  color: fontWeight === opt.value ? '#7c3aed' : '#5a6474',
                  transition: 'all 0.12s',
                  whiteSpace: 'nowrap',
                }}
              >
                {opt.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Text color */}
        <Box>
          <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Color</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="color"
              value={color}
              onChange={e => updFont({ color: e.target.value })}
              style={{ width: 32, height: 32, border: '1.5px solid #e4e9f0', borderRadius: 6, padding: 2, cursor: 'pointer', background: 'none', flexShrink: 0 }}
            />
            <input
              type="text"
              value={color}
              onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) updFont({ color: e.target.value }); }}
              style={{ flex: 1, border: '1px solid #e4e9f0', borderRadius: 6, padding: '5px 8px', fontSize: 12, fontFamily: 'monospace', color: '#2c3444', outline: 'none', background: '#f8f9fb' }}
            />
          </Box>
        </Box>

        {/* Align */}
        <Box>
          <Box sx={{ fontSize: 10, fontWeight: 600, color: '#b0bac8', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Align</Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {alignOptions.map(opt => (
              <Box
                key={opt.value}
                onClick={() => updFont({ textAlign: opt.value })}
                sx={{
                  flex: 1, height: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '7px', cursor: 'pointer',
                  border: `1px solid ${textAlign === opt.value ? '#7c3aed' : '#e4e9f0'}`,
                  background: textAlign === opt.value ? alpha('#7c3aed', 0.08) : '#f8f9fb',
                  color: textAlign === opt.value ? '#7c3aed' : '#9ea8b3',
                  transition: 'all 0.12s',
                  '&:hover': { borderColor: alpha('#7c3aed', 0.4), color: '#7c3aed' },
                }}
              >
                {opt.icon}
              </Box>
            ))}
          </Box>
        </Box>

      </Box>
    </PortalPopover>
  );
}
